import React, { useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import {
  BottomSheetModal,
  BottomSheetBackdrop,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Defs, RadialGradient, Stop, Rect } from 'react-native-svg';

interface Props {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Sticky bottom area rendered against a white background, separated from
   * the gradient body. Use for primary CTAs per Figma 6701:51001
   * (Header_6701_51003 + Group_6701_51035).
   */
  footer?: React.ReactNode;
}

/**
 * StageSheetShell — Bottom-sheet shell shared by CurrentStageSheet and
 * StageTransitionSheet. Mirrors the canonical bottom-sheet pattern used by
 * trail and meetings (BottomSheetModal + BottomSheetBackdrop, dynamic sizing,
 * pan-down to close, native handle indicator) so position, animation and
 * backdrop stay consistent across the app.
 *
 * Visual structure (Figma 6701:51001):
 *  ┌───────────────────────────┐  ← rounded top, radial gradient bg
 *  │   handle  •  Seu estágio  │  ← 56px header, centered title, close X
 *  │   illustration            │
 *  │   stage name              │  ← gradient body (children)
 *  │   description             │
 *  ├───────────────────────────┤  ← white footer bg
 *  │   [ Ok, entendi ]         │  ← sticky CTA (footer prop)
 *  └───────────────────────────┘
 */
export default function StageSheetShell({
  visible,
  title,
  onClose,
  children,
  footer,
}: Props) {
  const modalRef = useRef<BottomSheetModal>(null);
  const insets = useSafeAreaInsets();
  const screenHeight = Dimensions.get('window').height;
  // Tracks whether the sheet is currently presented (not just "has been
  // presented once"). Reset on close so we never call dismiss() twice.
  const isPresentedRef = useRef(false);

  useEffect(() => {
    if (visible) {
      isPresentedRef.current = true;
      modalRef.current?.present();
    } else if (isPresentedRef.current) {
      // Only dismiss programmatically when the sheet is still open. A drag /
      // backdrop dismiss already closed it (and cleared this ref via
      // handleDismiss), so calling dismiss() again on an already-dismissed
      // sheet corrupts BottomSheetModalProvider state and blocks the next
      // present() until the app is restarted.
      isPresentedRef.current = false;
      modalRef.current?.dismiss();
    }
  }, [visible]);

  const handleDismiss = useCallback(() => {
    // Fires for gesture/backdrop dismiss as well as programmatic dismiss().
    // Clear the flag first so the effect above doesn't re-dismiss.
    isPresentedRef.current = false;
    onClose();
  }, [onClose]);

  const renderBackdrop = useCallback(
    (props: any) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        opacity={0.4}
        pressBehavior="close"
        style={[props.style, { height: screenHeight }]}
      />
    ),
    [screenHeight],
  );

  return (
    <BottomSheetModal
      ref={modalRef}
      enableDynamicSizing
      maxDynamicContentSize={screenHeight * 0.9}
      onDismiss={handleDismiss}
      enablePanDownToClose
      backgroundStyle={styles.sheetBackground}
      handleIndicatorStyle={styles.handle}
      backdropComponent={renderBackdrop}
      animateOnMount
    >
      <BottomSheetView style={styles.container}>
        {/* Radial gradient fills the sheet beneath the body. The footer
            sits below it on a solid white background. */}
        <Svg
          style={StyleSheet.absoluteFillObject}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <Defs>
            <RadialGradient
              id="stageBg"
              cx="50%"
              cy="100%"
              rx="117.3%"
              ry="76.58%"
              fx="50%"
              fy="100%"
            >
              <Stop offset="5%" stopColor="#669AB7" stopOpacity={1} />
              <Stop offset="100%" stopColor="#E6EBF0" stopOpacity={1} />
            </RadialGradient>
          </Defs>
          <Rect x="0" y="0" width="100%" height="100%" fill="url(#stageBg)" />
        </Svg>

        <View style={styles.header}>
          <Text style={styles.title} numberOfLines={1}>
            {title}
          </Text>
          <TouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
            accessibilityRole="button"
            accessibilityLabel="Fechar"
          >
            <Ionicons name="close" size={20} color="#151515" />
          </TouchableOpacity>
        </View>

        <View style={styles.body}>{children}</View>

        {footer ? (
          <View
            style={[
              styles.footer,
              {
                paddingBottom:
                  Math.max(insets.bottom, Platform.OS === 'ios' ? 16 : 12),
              },
            ]}
          >
            {footer}
          </View>
        ) : (
          <View style={{ height: Math.max(insets.bottom, 12) }} />
        )}
      </BottomSheetView>
    </BottomSheetModal>
  );
}

/**
 * Per-stage illustration. RJ-11 will provide one image per stage; for now the
 * "investing" illustration ships as the default while the remaining 4 are
 * produced. When the rest land, swap this for a `stageNumber → asset` map.
 */
export const StagePlaceholderIllustration = ({
  size = 260,
}: { size?: number } = {}) => (
  <Image
    source={require('../assets/images/stage-investing.png')}
    style={{ width: size, height: size }}
    resizeMode="contain"
  />
);

const styles = StyleSheet.create({
  sheetBackground: {
    backgroundColor: 'transparent',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  handle: {
    backgroundColor: 'rgba(21, 21, 21, 0.18)',
    width: 36,
    height: 4,
  },
  container: {
    overflow: 'hidden',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  header: {
    height: 56,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    textAlign: 'center',
    lineHeight: 14,
  },
  closeButton: {
    position: 'absolute',
    right: 24,
    top: 16,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  footer: {
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingTop: 16,
  },
});
